/**
 * Отзыв публикации — `POST …/retract {reason, expected_brand}`.
 *
 * `expected_brand` — бренд ЭКРАНА (`screenBrand`, адрес страницы; 2026-10-25, изоляция
 * брендов): удалить сообщение из канала чужого бренда, глядя на экран своего, — ровно та
 * ошибка, ради которой поле заведено (`RetractBodySchema` контрактов). Бэкенд сверяет его с
 * брендом строки черновика; после перехода `DraftPanel` они равны, а расхождение значит, что
 * экран остался без перехода. `model.brand` здесь не годится: сверка сравнивала бы строку
 * черновика саму с собой. Канала в теле нет: отзыв подписывается на канал, куда пост ушёл, а
 * не выбирается диалогом.
 *
 * Окно 48 часов не наше правило: столько бот может удалить собственное сообщение в Telegram.
 * Значение и коды отказа сверены с `services/backend/src/domain/retract.ts`
 * (`RETRACT_WINDOW_MS = 172_800_000`, `retract_window_expired`, `retraction_in_flight`), а не
 * придуманы здесь.
 *
 * ── Откуда берётся начало окна ──
 *
 * Момента публикации (`publication_job.published_at_ms`) в модели рендера нет: 09 §9.1
 * его не выводит. Берём самый ранний достоверный «сообщение уже в канале» из того, что
 * есть: `audit.finished_at` (публикатор закрыл попытку) → `sending_started_at` → начало
 * слота. Все три не позже настоящего `published_at_ms`, поэтому счётчик закрывается РАНЬШЕ
 * сервера, а не позже: интерфейс не пообещает того, что бэкенд отвергнет. Решающий голос
 * всё равно за бэкендом — он ответит 409 `retract_window_expired`, и мы покажем его ответ.
 *
 * Удержание здесь такое же, как на утверждении, и по той же причине: удаление поста из
 * канала подписчики уже видели, отменить его нельзя.
 */

import { isoToMs, nowMs } from '@smm/contracts';
import {
  BRAND_MISMATCH_REASON,
  postLabel,
  type RetractBody,
  SIGNER_DRAFT_BRAND_PINNED_REASON,
} from '@smm/contracts/schemas';
import { type JSX, useEffect, useState } from 'react';
import { ApiRequestError, post } from '../../api/client.ts';
import type { Publication, RenderModel, RetractResponse } from '../../api/types.ts';
import { Modal } from '../../components/Modal.tsx';
import { HoldToConfirm } from './HoldToConfirm.tsx';
import { signerPinnedText } from './phrases.ts';

/** 48 часов — `RETRACT_WINDOW_MS` из services/backend/src/domain/retract.ts. */
export const RETRACT_WINDOW_MS = 172_800_000;

const REASON_MAX = 500;

/** Начало окна: самая ранняя достоверная отметка «пост в канале». */
export function retractWindowStartMs(p: Publication): number {
  const candidate = p.audit?.finished_at ?? p.sending_started_at ?? p.slot_start_at;
  return isoToMs(candidate);
}

/** «41 ч 12 мин» — без секунд: секунды в окне на двое суток создают ложную точность. */
export function formatRemaining(ms: number): string {
  if (ms <= 0) return 'окно истекло';
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes} мин`;
  return `${hours} ч ${minutes} мин`;
}

export interface RetractDialogProps {
  readonly model: RenderModel;
  /** Бренд экрана (адреса) — ТОЛЬКО для `expected_brand` (шапка файла). */
  readonly screenBrand: string;
  readonly publication: Publication;
  readonly onChanged: () => void;
  readonly onClose: () => void;
}

export function RetractDialog({
  model,
  screenBrand,
  publication,
  onChanged,
  onClose,
}: RetractDialogProps): JSX.Element {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(() => nowMs());

  // Минутная точность подписи не требует секундного тика, но окно может истечь, пока диалог
  // открыт, — и тогда кнопка обязана погаснуть сама, а не после следующего клика.
  useEffect(() => {
    const id = setInterval(() => {
      setNow(nowMs());
    }, 1000);
    return () => {
      clearInterval(id);
    };
  }, []);

  const endsAt = retractWindowStartMs(publication) + RETRACT_WINDOW_MS;
  const remaining = endsAt - now;
  const expired = remaining <= 0;

  const trimmed = reason.trim();
  const ready = trimmed.length > 0 && trimmed.length <= REASON_MAX && !expired;

  const submit = (): void => {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    const body: RetractBody = { reason: trimmed, expected_brand: screenBrand };
    post<RetractResponse>(
      `/api/v1/drafts/${model.draft_id}/versions/${model.version_no}/retract`,
      body,
    )
      .then(() => {
        onChanged();
        onClose();
      })
      .catch((e: unknown) => {
        if (e instanceof ApiRequestError) {
          const details = e.details;
          const why =
            typeof details === 'object' && details !== null && 'reason' in details
              ? String((details as { reason?: unknown }).reason)
              : null;
          if (why === 'retract_window_expired') {
            setError('Прошло больше 48 часов — Telegram уже не удалит это сообщение.');
          } else if (why === 'retraction_in_flight') {
            setError('Отзыв по этой публикации уже в работе — дождитесь отчёта публикатора.');
          } else if (why === SIGNER_DRAFT_BRAND_PINNED_REASON) {
            // Обновление экрана не снимет отказ: не зовём `onChanged`, чтобы не обещать этого.
            setError(signerPinnedText(e.message, 'Отзыв не подписан'));
          } else if (why === BRAND_MISMATCH_REASON) {
            setError(
              `${e.message}. Отзыв не подписан: пост принадлежит не тому бренду, что показан на экране. Обновите экран.`,
            );
            onChanged();
          } else if (e.status === 409) {
            setError(e.message);
            onChanged();
          } else {
            setError(e.message);
          }
        } else {
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Modal
      title="Отзыв публикации"
      subtitle={`${postLabel(model.post_no)} · v${model.version_no}`}
      onClose={onClose}
    >
      <div className={expired ? 'val val-err' : 'val val-warn'}>
        <span>
          {expired
            ? 'Окно 48 часов истекло: бот больше не может удалить своё сообщение. Пост остаётся в канале.'
            : `До конца окна 48 часов осталось ${formatRemaining(remaining)}. После этого Telegram сообщение уже не удалит.`}
        </span>
      </div>

      <div className="stack" style={{ gap: 8 }}>
        <label className="lbl" htmlFor="retract-reason" style={{ margin: 0 }}>
          Причина отзыва — попадёт в журнал и в ключ отзыва
        </label>
        <textarea
          id="retract-reason"
          className="inp"
          rows={3}
          maxLength={REASON_MAX}
          value={reason}
          disabled={expired}
          placeholder="Например: в цифре ошибка, пост вводит в заблуждение."
          onChange={(e) => {
            setReason(e.target.value);
          }}
        />
        <div className="lmeter">
          <span className="cnt num">
            {[...trimmed].length} / {REASON_MAX}
          </span>
          <span className="unit">символов</span>
        </div>
      </div>

      <div className="notice">
        Отзыв выпускает отдельный ключ и ставит публикатору задачу удалить сообщение. Подписчики,
        уже видевшие пост, об этом не узнают: удаление убирает сообщение, а не факт прочтения.
        Версия останется «опубликована», пока публикатор не отчитается об удалении.
      </div>

      {error === null ? null : (
        <div className="val val-err">
          <span>{error}</span>
        </div>
      )}

      <div className="stack" style={{ gap: 8 }}>
        <HoldToConfirm label="отзываю публикацию" disabled={!ready || busy} onConfirm={submit} />
        <div className="micro" style={{ textAlign: 'center' }}>
          удержание 800 мс · отпустите раньше — отмена
        </div>
        <button type="button" className="btn btn-ghost btn-w" onClick={onClose}>
          Отмена
        </button>
      </div>
    </Modal>
  );
}
